import { Injectable, inject } from '@angular/core';
import { singleConnect } from '@core/utils';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import {
	Action,
	State,
	StateToken,
	createPropertySelectors,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { FirestoreRepository } from '@core/services';
import { ClientIntakeForm } from '@models/client-intake';
import { of, retry } from 'rxjs';

import { FBClientIntakeFormsRepo } from '../clientIntakeForms.firebase.repo';

import type { FBClientIntakeFormsStateModel } from './clientIntakeForms.firebase.models';
import {
	ClearClientIntakeFormIds,
	ClearCurrentIntakeFormConnection,
	SetClientIntakeForm,
	SetClientIntakeFormIds,
	SetClientIntakeFormsConnection,
} from './clientIntakeForms.firebase.actions';

export const STATE_TOKEN = new StateToken<FBClientIntakeFormsStateModel>(
	'FBClientIntakeForms',
);

export const $PROPS = createPropertySelectors(STATE_TOKEN);

export const CLIENT_INTAKE_FORMS_STATE_DEFAULTS: FBClientIntakeFormsStateModel =
	{
		query: {
			connected: true,
			pathParams: null,
		},
		item: null,
		businessId: null,
		matterId: null,
		clientIntakeFormId: null,
	};

@State<FBClientIntakeFormsStateModel>({
	name: STATE_TOKEN,
	defaults: CLIENT_INTAKE_FORMS_STATE_DEFAULTS,
})
@Injectable()
export class FBClientIntakeFormsState implements NgxsOnInit {
	private subCollectionRepo = inject(FBClientIntakeFormsRepo);
	private repo: FirestoreRepository<ClientIntakeForm> | null = null;
	static _props = $PROPS;

	ngxsOnInit(): void {
		singleConnect(
			FBClientIntakeFormsState._props.query,
			(query) => {
				if (
					!query.pathParams?.businessId ||
					!query.pathParams?.matterId ||
					!query.pathParams?.clientIntakeFormId
				) {
					this.repo = null;
					return of(null);
				}
				const collectionParams = {
					businessId: query.pathParams.businessId,
					matterId: query.pathParams.matterId,
				};
				this.repo = this.subCollectionRepo.getCollection(collectionParams);
				return this.repo
					.getById(query.pathParams.clientIntakeFormId)
					.pipe(retry({ count: 10, delay: 500 }));
			},
			(data) => new SetClientIntakeForm(data),
		);
	}

	@Action(SetClientIntakeFormsConnection)
	setClientIntakeFormsConnection(
		ctx: StateContext<FBClientIntakeFormsStateModel>,
		action: SetClientIntakeFormsConnection,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: action.connect,
					pathParams: action.pathParams,
				}),
			}),
		);
	}

	@Action(SetClientIntakeForm)
	setClientIntakeForm(
		ctx: StateContext<FBClientIntakeFormsStateModel>,
		{ item }: SetClientIntakeForm,
	): void {
		ctx.patchState({
			item: item,
		});
	}

	@Action(ClearCurrentIntakeFormConnection)
	clearCurrentIntakeFormConnection(
		ctx: StateContext<FBClientIntakeFormsStateModel>,
	): void {
		ctx.setState(
			patch({
				query: patch({
					connected: false,
					pathParams: null,
				}),
			}),
		);
	}

	@Action(SetClientIntakeFormIds)
	setClientIntakeFormIds(
		ctx: StateContext<FBClientIntakeFormsStateModel>,
		action: SetClientIntakeFormIds,
	): void {
		const { businessId, matterId, clientIntakeFormId } = action.ids;

		ctx.setState(
			patch({
				businessId,
				matterId,
				clientIntakeFormId,
			}),
		);
	}

	@Action(ClearClientIntakeFormIds)
	clearClientIntakeFormIds(
		ctx: StateContext<FBClientIntakeFormsStateModel>,
	): void {
		ctx.setState(
			patch({
				businessId: null,
				matterId: null,
				clientIntakeFormId: null,
			}),
		);
	}
}
